


























































import Vue from 'vue';
import { defineComponent, ref, computed, Ref, reactive } from '@vue/composition-api';
import { parseISO, isDate, format, parse, isEqual } from 'date-fns';
import { generateRandomId, parseDate } from '../helpers';
import { useWrapperClasses, useInputClasses, useExposeFocus, useDefaultInputFlags } from './abstractions';

export default defineComponent({
    name: 'WokInputDate',

    props: {
        name: {
            type: String,
            default: ''
        },
        value: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            dafault: ''
        },
        error: {
            type: Object,
            default: null
        },
        placeholder: {
            type: String,
            default: ''
        },
        disabled: {
            type: [Boolean, String],
            default: false
        },
        readonly: {
            type: [Boolean, String],
            default: false
        },
        resetValue: {
            type: String,
            default: ''
        },
        showReset: {
            type: [Boolean, String],
            default: false
        },
        showCalendarAction: {
            type: [Boolean, String],
            default: true
        },
        actionSequentialFocus: {
            type: [Boolean, String],
            default: false
        },
        showCalendarOnFocus: {
            type: [Boolean, String],
            default: false
        },
        focus: {
            type: Boolean,
            default: null
        },
        locale: {
            type: Object,
            default: null
        }
    },

    model: { prop: 'value', event: 'update:value' },

    setup(props, context) {
        const inputElement = ref() as Ref<HTMLInputElement>;

        const state = reactive({
            pickerVisible: false,
            pickerValue: computed(() => props.value),
        });

        const id = generateRandomId(props.name);

        const { isReadOnly, isDisabled, resetActionVisible, actionSequentialFocusActive } = useDefaultInputFlags(props);

        const { focused, onFocus, onBlur } = useExposeFocus(inputElement, context.emit, props);

        const hasActions = computed(() => (resetActionVisible.value || calendarActionVisible.value)
                                        && !isReadOnly.value
                                        && !isDisabled.value);

        const calendarActionVisible = computed(() => props.showCalendarAction.toString() == 'true');

        const calendarOnFocusActive = computed(() => props.showCalendarOnFocus.toString() == 'true');

        // We don't need to export this, but it should be reactive to correctly trigger `formattedValue`.
        let focusValue = ref('');

        const formattedValue = computed(() => {
            let v = props.value ? parseISO(props.value) : null;

            // When focused format the date using a short string according to the locale.
            if (focused.value) {
                let fv = focusValue.value; focusValue.value = '';
                return fv || inputElement.value.value;
            }
            // When not focused format the date according to the given locale.
            else  {
                if (v && isDate(v)) {
                    return format(v, "PPPP", { locale: props.locale?.date })
                }
            }

            return '';
        });

        const onInput = (evt: Event) => {
            if (evt.target instanceof HTMLInputElement) {
                let v = parseDate(evt.target.value, props.locale?.date);
                if (v && !isNaN(v.getDate())) {
                    context.emit('update:value', format(v, "yyyy-MM-dd"));
                }
            }
        };

        const onReset = () => {
            context.emit('update:value', props.resetValue);
        };

        const onFocusExtension = () => {
            onFocus();
            if (calendarOnFocusActive.value) {
                state.pickerVisible = true;
            }
            if (props.value) {
                focusValue.value = format(parseISO(props.value), "P", { locale: props.locale?.date });
                Vue.nextTick(() => inputElement.value.select());
            }
        };

        const onUpdatePickerValue = (value: string) => {
            context.emit('update:value', value);
        }

        return {
            inputElement,
            id,
            state,
            wrapperClasses : useWrapperClasses(props, context.attrs),
            inputClasses : useInputClasses('date', hasActions),
            hasActions,
            isReadOnly,
            isDisabled,
            resetActionVisible,
            calendarActionVisible,
            actionSequentialFocusActive,
            formattedValue,
            onInput,
            onReset,
            onFocus: onFocusExtension,
            onBlur,
            onUpdatePickerValue
        }
    }
})
